import React, { useState, useRef, useEffect } from 'react'
import {
    CheckCircleIcon,
    CircleIcon,
    CaretDownIcon,
    CaretUpIcon,
    XIcon,
    ArrowRightIcon,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { useOnboarding, ONBOARDING_STEPS } from '../../../../api/hooks/onboarding/useOnboarding'
import { ONBOARDING_GRACE_PERIOD_DAYS } from '../../../../api/hooks/onboarding/onboardingApi'
import { useUserInfo } from '../../../../api/hooks/user/useUserInfo'
import { ScoopText, ScoopRow, ScoopColumn, ScoopMenu } from '../../../ui'
import { ROUTES } from '../../../../router/routes'
import styles from './OnboardingChecklist.module.scss'

/**
 * OnboardingChecklist - Displays progress through onboarding steps
 *
 * Shows a collapsible checklist in the NavBar with step completion status.
 * Collapses to a compact progress indicator when minimized.
 * Can be dismissed by user (hides but preserves progress).
 *
 * @param {boolean} isTourWorkspace - Whether user is on Tour Scoop workspace
 */
export const OnboardingChecklist = ({ isTourWorkspace = false }) => {
    const navigate = useNavigate()
    const [isExpanded, setIsExpanded] = useState(false)
    const prevCompletedCountRef = useRef(0)
    const buttonRef = useRef(null)
    const {
        onboarding,
        loadingOnboarding,
        isOnboardingComplete,
        isDismissed,
        dismiss,
        getStepsWithStatus,
        getCompletedCount,
        getProgressPercentage,
        isStepCompleted,
        completeStep,
    } = useOnboarding()
    const { user, loadingUser: isLoadingUser } = useUserInfo()

    // Auto-expand checklist whenever a new step is completed
    const completedCount = getCompletedCount()
    useEffect(() => {
        if (!loadingOnboarding && completedCount > prevCompletedCountRef.current) {
            setIsExpanded(true)
        }
        prevCompletedCountRef.current = completedCount
    }, [loadingOnboarding, completedCount])

    // Account longevity check - users with accounts older than grace period don't need onboarding
    const accountAgeDays = user?.createDate
        ? (Date.now() - user.createDate) / (1000 * 60 * 60 * 24)
        : 0
    const isExistingUser = accountAgeDays > ONBOARDING_GRACE_PERIOD_DAYS

    // Don't render if loading, data not yet available, onboarding is complete
    // (except in tour mode where we show it forever), dismissed, or existing user
    if (
        loadingOnboarding ||
        isLoadingUser ||
        !onboarding ||
        (isOnboardingComplete() && !isTourWorkspace) ||
        isDismissed() ||
        isExistingUser
    ) {
        return null
    }

    const steps = getStepsWithStatus()
    const totalSteps = steps.length
    const progressPercentage = getProgressPercentage()

    // Check if step 2 (OPEN_CHART) is completed
    const isStep2Completed = isStepCompleted(ONBOARDING_STEPS.OPEN_CHART)

    // Override step 3 label/description for Tour workspace
    const getStepDisplay = (step) => {
        if (isTourWorkspace && step.key === ONBOARDING_STEPS.SAVE_TO_CANVAS) {
            return {
                label: 'Connect Your Data',
                description: 'Upload your own data to unlock the full Scoop experience',
                isClickable: isStep2Completed,
                isDisabled: !isStep2Completed,
            }
        }
        return {
            label: step.label,
            description: step.description,
            isClickable: false,
            isDisabled: false,
        }
    }

    // Handle click on "Connect Your Data" step
    const handleStepClick = (step) => {
        if (isTourWorkspace && step.key === ONBOARDING_STEPS.SAVE_TO_CANVAS) {
            // Navigate to data upload - step completion happens after data processing finishes
            setIsExpanded(false)
            navigate(ROUTES.ONBOARDING, { state: { stepIndex: 1 } })
        }
    }

    const handleToggle = () => {
        setIsExpanded(!isExpanded)
    }

    const handleDismiss = (e) => {
        e.stopPropagation() // Prevent toggle when clicking dismiss
        dismiss()
    }

    const handleClose = () => {
        setIsExpanded(false)
    }

    return (
        <div className={styles.container}>
            {/* Collapsed View - Progress Badge */}
            <button
                ref={buttonRef}
                className={styles.progressBadge}
                onClick={handleToggle}
                aria-expanded={isExpanded}
                aria-label={`Onboarding progress: ${completedCount} of ${totalSteps} steps complete`}
            >
                <div className={styles.progressRing}>
                    <svg viewBox="0 0 36 36" className={styles.progressSvg}>
                        <path
                            className={styles.progressBg}
                            d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                        <path
                            className={styles.progressFill}
                            strokeDasharray={`${progressPercentage}, 100`}
                            d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                    </svg>
                    <ScoopText className={styles.progressText}>
                        {completedCount}/{totalSteps}
                    </ScoopText>
                </div>
                <ScoopText className={styles.progressLabel}>Get Started</ScoopText>
                {isExpanded ? (
                    <CaretUpIcon size={14} weight="bold" />
                ) : (
                    <CaretDownIcon size={14} weight="bold" />
                )}
            </button>

            {/* Expanded View - Checklist Menu */}
            <ScoopMenu
                open={isExpanded}
                anchorEl={buttonRef.current}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                className={styles.checklist}
            >
                <ScoopColumn role="list" aria-label="Onboarding checklist">
                    <ScoopRow
                        className={styles.checklistHeader}
                        justify="space-between"
                        align="center"
                    >
                        <ScoopText weight="semibold" size="sm">
                            Getting Started
                        </ScoopText>
                        <ScoopText size="xs" color="secondary">
                            {completedCount} of {totalSteps} complete
                        </ScoopText>
                    </ScoopRow>
                    <ScoopColumn className={styles.checklistItems}>
                        {steps.map((step) => {
                            const { label, description, isClickable, isDisabled } =
                                getStepDisplay(step)
                            const showAsClickable =
                                isTourWorkspace && step.key === ONBOARDING_STEPS.SAVE_TO_CANVAS
                            return (
                                <ScoopRow
                                    key={step.key}
                                    className={`${styles.checklistItem} ${step.completed ? styles.completed : ''} ${isClickable ? styles.clickable : ''} ${isDisabled ? styles.disabled : ''}`}
                                    role="listitem"
                                    align="flex-start"
                                    gap={12}
                                    onClick={isClickable ? () => handleStepClick(step) : undefined}
                                >
                                    {step.completed ? (
                                        <CheckCircleIcon
                                            size={20}
                                            weight="fill"
                                            className={styles.iconCompleted}
                                        />
                                    ) : (
                                        <CircleIcon size={20} className={styles.iconPending} />
                                    )}
                                    <ScoopColumn className={styles.stepContent} gap={2}>
                                        <ScoopText
                                            size="sm"
                                            weight="medium"
                                            className={styles.stepLabel}
                                        >
                                            {label}
                                        </ScoopText>
                                        <ScoopText
                                            size="xs"
                                            color="secondary"
                                            className={styles.stepDescription}
                                        >
                                            {description}
                                        </ScoopText>
                                    </ScoopColumn>
                                    {showAsClickable && (
                                        <ArrowRightIcon
                                            size={16}
                                            weight="bold"
                                            className={`${styles.arrowIcon} ${isDisabled ? styles.arrowDisabled : ''}`}
                                        />
                                    )}
                                </ScoopRow>
                            )
                        })}
                    </ScoopColumn>
                    {/* Dismiss button - hidden in tour mode since checklist should stay visible */}
                    {!isTourWorkspace && (
                        <button
                            className={styles.dismissButton}
                            onClick={handleDismiss}
                            aria-label="Don't show this again"
                        >
                            <XIcon size={12} weight="bold" />
                            <ScoopText size="xs" color="secondary">
                                Don't show this again
                            </ScoopText>
                        </button>
                    )}
                </ScoopColumn>
            </ScoopMenu>
        </div>
    )
}
